import React, {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import Share from "@mui/icons-material/Share";
import { Box, Checkbox, Chip, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import sanitizeHtml from "sanitize-html";

import { BaseLabel, Drawer, LoadingComponent } from "components";

import { useToast } from "hooks/toast";

import { useAppSelector } from "store/rootReducer";

import { LoadingContainer } from "pages/app/audiences/styles";

import { ItemDetailLine } from "components/ItemDetailLine";
import { StatusBadge } from "components/StatusBadge";

import {
  hearingFileService,
  IHearingFileDownloads,
} from "services/hearingFile";
import { MeetingMConfService } from "services/meetingMConf";

import close from "assets/close.svg";
import CameraIcon from "assets/svg/CameraIcon.svg";
import EditIcon from "assets/svg/EditIcon.svg";
import EyeIcon from "assets/svg/EyeIcon.svg";
import FileIcon from "assets/svg/FileIcon.svg";
import PencilIcon from "assets/svg/PencilIcon.svg";

import { isExternalSourcePlatform } from "utils/entities/hearingFilePlatform";
import { isMConfProvider } from "utils/providerUtils";

import { IItensDetails, IRightAudienceComponent } from "./interface";
import {
  Container,
  TopContainer,
  Title,
  Button,
  ExternalBadgeContainer,
  ProcessingVideoContainer,
  ShareDrawerContainer,
  ShareDrawerScroll,
  ShareSection,
  HelperText,
  EmailInputContainer,
  EmailChipsContainer,
  AttachmentControlLabel,
} from "./styles";
import { Page } from "./types";

export const RightAudienceComponent: React.FC<IRightAudienceComponent> = ({
  onClose,
  title,
  record,
  onRecordChange,
  baseBlob,
  container,
  hearing,
  disableFullView,
  hearingService,
}) => {
  const { t } = useTranslation();
  const notShowProcess = [10, 11, 14, 15];

  const [loading, setLoading] = useState(false);
  const [isShareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [emailList, setEmailList] = useState<string[]>([]);
  const [emailDraft, setEmailDraft] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [attachments, setAttachments] = useState<IHearingFileDownloads[]>([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [selectedAttachmentIds, setSelectedAttachmentIds] = useState<number[]>(
    [],
  );
  const [includeMainRecord, setIncludeMainRecord] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");

  useEffect(() => {
    if (record?.filename || !record?.id || !onRecordChange || videoUrl) return;
    let cancelled = false;

    const fetchRecordUrl = async () => {
      const response = await hearingFileService.getUrlById(record.id);
      const filename = response.body?.filename;

      if (!cancelled && filename) {
        onRecordChange({ ...record, filename });
      }
    };

    fetchRecordUrl();

    return () => {
      cancelled = true;
    };
  }, [record.filename, onRecordChange]);

  const { user: userRedux } = useAppSelector((e) => e.user);

  const { globalConfigurations } = useAppSelector(
    (store) => store.configurations,
  );

  const recordProviderId = globalConfigurations?.find(
    (config: any) => config.name === "RecordProvider",
  )?.value;

  const externalLinkEmailText = globalConfigurations?.find(
    (config: any) => config.name === "ExternalLinkTextEmail",
  )?.value;

  const expireInHearingFileUrlHours = Number(
    globalConfigurations?.find(
      (config: any) => config.name === "ExpireInHearingFileUrl",
    )?.value,
  );

  const { accessToken } = useAppSelector((state) => state.auth);

  const { addToast } = useToast();
  const navigate = useNavigate();

  const isValidEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  }, []);

  const mergeEmailLists = useCallback(
    (current: string[], candidates: string[]): string[] => {
      const normalizedCandidates = candidates
        .flatMap((candidate) => candidate.split(";"))
        .map((candidate) => candidate.trim())
        .filter((candidate) => candidate.length > 0);

      if (normalizedCandidates.length === 0) {
        return current;
      }

      const existingKeys = new Set(current.map((email) => email.toLowerCase()));
      let hasChanges = false;
      const nextEmails = [...current];

      normalizedCandidates.forEach((candidate) => {
        const normalizedKey = candidate.toLowerCase();

        if (existingKeys.has(normalizedKey)) {
          return;
        }

        existingKeys.add(normalizedKey);
        nextEmails.push(candidate);
        hasChanges = true;
      });

      return hasChanges ? nextEmails : current;
    },
    [],
  );

  const addEmailsFromInput = useCallback(
    (segments: string[]): boolean => {
      const validEmails: string[] = [];
      const invalidEmails: string[] = [];

      segments.forEach((segment) => {
        const trimmedSegment = segment.trim();
        if (trimmedSegment) {
          if (isValidEmail(trimmedSegment)) {
            validEmails.push(trimmedSegment);
          } else {
            invalidEmails.push(trimmedSegment);
          }
        }
      });

      if (invalidEmails.length > 0) {
        addToast({
          type: "warning",
          title: t("share.invalidEmail"),
          description: t("share.invalidEmailDescription", {
            emails: invalidEmails.join(", "),
          }),
        });
      }

      if (validEmails.length > 0) {
        setEmailList((previous) => mergeEmailLists(previous, validEmails));
      }

      return invalidEmails.length === 0;
    },
    [mergeEmailLists, isValidEmail, addToast],
  );

  const handleEmailInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;

      if (value.includes(";")) {
        const segments = value.split(";");
        const lastSegment = segments.pop() ?? "";
        const segmentsToAdd = segments.filter((seg) => seg.trim());

        if (segmentsToAdd.length > 0) {
          addEmailsFromInput(segmentsToAdd);
        }

        setEmailDraft(lastSegment);
        return;
      }

      setEmailDraft(value);
    },
    [addEmailsFromInput],
  );

  const handleEmailInputKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Enter") {
        event.preventDefault();

        if (emailDraft.trim()) {
          const isValid = addEmailsFromInput([emailDraft]);
          if (isValid) {
            setEmailDraft("");
          }
        }

        return;
      }

      if (event.key === ";" && !event.shiftKey) {
        event.preventDefault();

        if (emailDraft.trim()) {
          const isValid = addEmailsFromInput([emailDraft]);
          if (isValid) {
            setEmailDraft("");
          }
        }

        return;
      }

      if (
        event.key === "Backspace" &&
        emailDraft.length === 0 &&
        emailList.length > 0
      ) {
        event.preventDefault();
        setEmailList((previous) => previous.slice(0, -1));
      }
    },
    [addEmailsFromInput, emailDraft, emailList.length],
  );

  const handleEmailInputBlur = useCallback(() => {
    if (!emailDraft.trim()) {
      return;
    }

    const isValid = addEmailsFromInput([emailDraft]);
    if (isValid) {
      setEmailDraft("");
    }
  }, [addEmailsFromInput, emailDraft]);

  const handleRemoveEmail = useCallback((target: string) => {
    setEmailList((previous) => previous.filter((email) => email !== target));
  }, []);

  const formatExpirationDate = useCallback(
    (hoursToAdd?: number): string | undefined => {
      if (!hoursToAdd || Number.isNaN(hoursToAdd)) {
        return undefined;
      }

      const now = new Date();
      now.setHours(now.getHours() + hoursToAdd);

      try {
        return new Intl.DateTimeFormat("pt-BR", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(now);
      } catch {
        return now.toISOString();
      }
    },
    [],
  );

  const handleOpenShareDrawer = useCallback(() => {
    const template = externalLinkEmailText || "";
    const expirationDate = formatExpirationDate(expireInHearingFileUrlHours);

    let processedTemplate = template;

    if (title) {
      processedTemplate = processedTemplate.replace(/{title}/g, title);
    }

    processedTemplate = processedTemplate.replace(/{customText}/g, "");

    processedTemplate = processedTemplate.replace(
      /{link}/g,
      "<strong>{link}</strong>",
    );

    if (expirationDate) {
      processedTemplate = processedTemplate.replace(
        /{expireDate}/g,
        expirationDate,
      );
    }

    setEmailList([]);
    setEmailDraft("");
    setEmailBody(processedTemplate);
    setSelectedAttachmentIds([]);
    setIncludeMainRecord(false);
    setShareDrawerOpen(true);
  }, [
    externalLinkEmailText,
    expireInHearingFileUrlHours,
    formatExpirationDate,
    title,
  ]);

  const fetchAttachments = useCallback(async () => {
    if (!record?.hearingId) {
      setAttachments([]);
      return;
    }

    setAttachmentsLoading(true);
    try {
      const { status, data } =
        await hearingFileService.getAllDownloadsWithoutUrlByHearingId(
          record.hearingId,
        );
      if (status === 200) {
        const list = (data?.data ?? []).filter(
          (attachment: any) => attachment.type !== 1 && attachment.type !== 6,
        );
        setAttachments(list);
      } else {
        setAttachments([]);
      }
    } catch {
      setAttachments([]);
    } finally {
      setAttachmentsLoading(false);
    }
  }, [record?.hearingId]);

  useEffect(() => {
    if (isShareDrawerOpen) {
      fetchAttachments();
    }
  }, [fetchAttachments, isShareDrawerOpen]);

  const formatAttachmentName = useCallback((filename?: string) => {
    if (!filename) {
      return t("attachments.noName");
    }

    const cleanPath = filename.split("?")[0].split("#")[0];
    const parts = cleanPath.split(/[\\/]/);
    const last = parts[parts.length - 1] || cleanPath;

    try {
      return decodeURIComponent(last);
    } catch {
      return last;
    }
  }, []);

  const toggleAttachmentSelection = useCallback((hearingFileId: number) => {
    setSelectedAttachmentIds((prev) =>
      prev.includes(hearingFileId)
        ? prev.filter((id) => id !== hearingFileId)
        : [...prev, hearingFileId],
    );
  }, []);

  const handleCloseShareDrawer = useCallback(() => {
    setShareDrawerOpen(false);
  }, []);

  const handleShareSubmit = useCallback(async () => {
    const mergedEmails = mergeEmailLists(
      emailList,
      emailDraft.trim() ? [emailDraft] : [],
    );

    if (mergedEmails.length === 0) {
      addToast({
        type: "warning",
        title: t("common.attention"),
        description: t("share.addAtLeastOneEmail"),
      });
      return;
    }

    const strippedEmailBody = emailBody.replace(/<[^>]*>/g, "").trim();
    if (!strippedEmailBody) {
      addToast({
        type: "warning",
        title: t("common.attention"),
        description: t("share.emptyMessage"),
      });
      return;
    }

    setEmailList(mergedEmails);
    setEmailDraft("");
    setLoading(true);

    try {
      const attachmentIds = [
        ...(includeMainRecord && typeof record?.id === "number"
          ? [record.id]
          : []),
        ...selectedAttachmentIds,
      ].filter((value): value is number => typeof value === "number");
      const uniqueAttachmentIds = Array.from(new Set(attachmentIds));

      const response = await hearingFileService.sendLink(
        record?.id,
        mergedEmails,
        uniqueAttachmentIds,
      );

      if (response.success) {
        addToast({
          type: "success",
          title: t("common.success"),
          description: response.message || "Link compartilhado com sucesso.",
        });
        setShareDrawerOpen(false);
      } else {
        addToast({
          type: "error",
          title: t("common.error"),
          description: response.message || t("share.error"),
        });
      }
    } catch (error) {
      addToast({
        type: "error",
        title: t("common.error"),
        description: t("share.unexpectedError"),
      });
    } finally {
      setLoading(false);
    }
  }, [
    emailBody,
    emailDraft,
    emailList,
    mergeEmailLists,
    record?.id,
    addToast,
    selectedAttachmentIds,
    includeMainRecord,
  ]);

  const canRejoinHearingSession = () => {
    if (recordProviderId === "1") {
      return record.status === 12 && record?.userId === userRedux?.id;
    }

    return record.status === 12 || record.status === 11;
  };

  const joinLocal = () => {
    navigate(
      `/recording/local?hearingId=${record?.hearingId}&hearingFileId=${record?.id}`,
      {
        state: {
          procedureNumber: hearing?.procedureNumber,
          procedureCustomerSourceName: hearing?.procedureCustomerSourceName,
        },
      },
    );
  };

  const showCannotRecordToast = () => {
    addToast({
      type: "error",
      title: t("recordings.messages.cannotRecord"),
      description: t("audiences.messages.checkCreated"),
    });
  };

  const applyDigitroCookies = (response: any, responseRenewToken: any) => {
    if (
      responseRenewToken &&
      responseRenewToken.data &&
      responseRenewToken.data.deviceToken
    ) {
      document.cookie = `${responseRenewToken.data.deviceToken}`;
      document.cookie = `${responseRenewToken.data.authToken}`;
      return;
    }

    document.cookie = `${response.data.token}`;
    document.cookie = `${response.data.sessionToken}`;
  };

  const navigateToDigitroRoom = (response: any) => {
    setTimeout(() => {
      navigate(
        `/recording/una?hearingId=${record?.hearingId}&hearingFileId=${record?.id}`,
        {
          state: {
            ...record,
            hearingId: record?.id,
            hearingAudienceId: record?.hearingId,
            hearingFileId: record.id,
            urlModerator: response.data.urlModerator,
            urlAttendee: response.data.urlAttendee,
            videoRoomId: response.data.videoRoom?.id,
            authToken: response.data.token,
            deviceToken: response.data.sessionToken,
            backToHearing: true,
          },
        },
      );
      setLoading(false);
    }, 10000);
  };

  const rejoinHearingSessionDigitro = async () => {
    if (!accessToken || !record) {
      return;
    }

    let responseRenewToken: any;

    if (hearing.recordMode === 2) {
      joinLocal();
    }

    setLoading(true);

    try {
      const response = await hearingService.getByHearingFileId(
        accessToken,
        record?.id,
      );

      if (response?.data?.videoRoom?.id) {
        responseRenewToken = await hearingService.renewTokens(
          accessToken,
          response.data.videoRoom?.id,
        );
      }

      if (response === null || response.status !== true) {
        showCannotRecordToast();
        setLoading(false);
        return;
      }

      applyDigitroCookies(response, responseRenewToken);
      navigateToDigitroRoom(response);
    } catch {
      showCannotRecordToast();
      setLoading(false);
    }
  };

  const rejoinHearingSessionMConf = async () => {
    if (accessToken && record) {
      if (hearing.recordMode === 2) {
        joinLocal();
      }

      setLoading(true);
      const response = await hearingService.getByHearingFileId(
        accessToken,
        record?.id,
      );

      const joinResp = await MeetingMConfService.joinMeeting(record?.id);

      if (!joinResp.status) {
        addToast({
          type: "error",
          title: t("recordings.messages.mconfTitle"),
          description: joinResp.message ?? t("recordings.messages.mconfError"),
        });
        setLoading(false);
        return;
      }

      if (response !== null && response.status === true) {
        const joinUrl = joinResp.data?.message ?? "";

        setTimeout(() => {
          navigate(
            `/recording/mconf?hearingId=${record?.hearingId}&hearingFileId=${record?.id}&attendantId=${record?.attendantId || ""}`,
            {
              state: {
                procedureNumber: hearing?.procedureNumber,
                procedureCustomerSourceName:
                  hearing?.procedureCustomerSourceName,
                mconfJoinUrl: joinUrl,
              },
            },
          );
          setLoading(false);
        }, 10000);
      } else {
        addToast({
          type: "error",
          title: t("recordings.messages.cannotRecord"),
          description: t("audiences.messages.checkCreated"),
        });
        setLoading(false);
      }
    }
  };

  const showFullRecording = () => {
    navigate(
      `/audiences/fullrecording?audienceNumber=${hearing.procedureNumber}&hearingFileId=${record.id}&placeId=${hearing.placeId}&attendantId=${record.attendantId || ""}`,
      { state: { audience: hearing } },
    );
  };

  const fullViewNavigate = (page: number) => {
    navigate(
      `/audiences/fullview?audienceNumber=${hearing.procedureNumber}&hearingFileId=${record.id}&hearingId=${hearing.id}&audienceVideo=${record?.filename}&placeId=${hearing.placeId}&platform=${record.platform ?? ""}&attendantId=${record.attendantId || ""}&hiddenFields=ata`,
      { state: { audience: hearing, page } },
    );
  };

  useEffect(() => {
    if (
      record?.filename &&
      (record?.filename.startsWith("http") ||
        record?.filename.startsWith("https"))
    ) {
      setVideoUrl(record.filename);
    }
  }, [record?.filename]);

  const isMconf = isMConfProvider(hearing?.recordMode, recordProviderId);

  const listLines: Array<IItensDetails> = [
    {
      icon: EyeIcon,
      label: t("recordings.fullView"),
      disabled: disableFullView || notShowProcess.includes(record.status),
      onClick: () => fullViewNavigate(Page.MARCATIONS),
      permission: true,
      hr: false,
      hidden: isMconf || record.upload,
    },
    {
      icon: EyeIcon,
      label: t("recordings.fullView"),
      disabled: disableFullView || notShowProcess.includes(record.status),
      onClick: () => showFullRecording(),
      permission: true,
      hr: false,
      hidden: !isMconf || record.upload,
    },
    {
      icon: FileIcon,
      label: t("transcription.title"),
      disabled: disableFullView || notShowProcess.includes(record.status),
      onClick: () => fullViewNavigate(Page.TRANSCRIPTION),
      permission: true,
      hr: false,
      hidden: isMconf,
    },
    {
      icon: EditIcon,
      label: isMconf ? t("markings.titleWithMinutes") : t("markings.title"),
      disabled: disableFullView || notShowProcess.includes(record.status),
      onClick: () => fullViewNavigate(Page.MARCATIONS),
      permission: true,
      hr: true,
    },
    {
      icon: CameraIcon,
      label: t("recordings.joinRecording"),
      disabled:
        !canRejoinHearingSession() ||
        [10, 14, 15].includes(record.status) ||
        hearing.recordMode === 2,
      onClick: () => {
        if (!canRejoinHearingSession()) {
          addToast({
            type: "error",
            title: t("common.actionNotExecuted"),
            description: t("audiences.messages.onlyWaitingStatus"),
          });
        } else {
          if (recordProviderId === "2") {
            rejoinHearingSessionMConf();
            return;
          }
          rejoinHearingSessionDigitro();
        }
      },
      permission: canRejoinHearingSession(),
      hr: false,
    },
    {
      icon: PencilIcon,
      label: t("recordings.editInfo"),
      disabled: true,
      onClick: () => {},
      permission: true,
      hr: false,
    },
    {
      icon: <Share sx={{ color: "#7887A9" }} />,
      label: t("share.externalLink"),
      disabled: disableFullView || notShowProcess.includes(record.status),
      onClick: handleOpenShareDrawer,
      permission: true,
      hr: false,
    },
  ];

  return (
    <>
      <Container>
        <TopContainer>
          <Title>{title}</Title>
          <Button onClick={onClose}>
            <img src={close} alt="" />
          </Button>
        </TopContainer>

        {record.status === 13 ? (
          <>
            {isExternalSourcePlatform(record.platform) && (
              <ExternalBadgeContainer>
                <StatusBadge
                  id="external_video_badge_right"
                  label={t("recordings.externalVideo")}
                  type="inactive"
                />
              </ExternalBadgeContainer>
            )}
            <video
              key={videoUrl}
              width="100%"
              height="100%"
              controls
              controlsList="nodownload"
              onContextMenu={(event) => event.preventDefault()}
            >
              <source src={videoUrl} type="video/mp4" />
              {t("recordings.videoNotSupported")}
            </video>
          </>
        ) : (
          !notShowProcess.includes(record.status) && (
            <ProcessingVideoContainer>
              <strong>{t("common.processing")}</strong>
            </ProcessingVideoContainer>
          )
        )}
        {listLines
          .filter((line) => !line.hidden)
          .map((line) => (
            <ItemDetailLine
              key={line.label}
              disabled={line.disabled}
              onClick={line.onClick}
              icon={
                typeof line.icon === "string" ? (
                  <img src={line.icon} alt={line.label} />
                ) : (
                  line.icon
                )
              }
              label={line.label}
              hr={line.hr}
              permissions={line.permission}
            />
          ))}
      </Container>
      {loading && (
        <LoadingContainer>
          <LoadingComponent loading={loading} />
        </LoadingContainer>
      )}
      <Drawer
        id="share-external-link-drawer"
        title={t("share.externalLink")}
        applyLabel={t("common.send")}
        open={isShareDrawerOpen}
        onApply={handleShareSubmit}
        onClose={handleCloseShareDrawer}
        paddingLeft={32}
        paddingRight={32}
        paddingTop={24}
        paddingBottom={32}
        bodyHeight={"calc(100vh - 240px)"}
      >
        <ShareDrawerScroll>
          <ShareDrawerContainer>
            <ShareSection>
              <BaseLabel fontSize="16px" fontWeight="600">
                {t("share.recipients")}
              </BaseLabel>
              <HelperText>{t("share.recipientsHelper")}</HelperText>
              <EmailInputContainer>
                {emailList.length > 0 && (
                  <EmailChipsContainer>
                    {emailList.map((email) => (
                      <Chip
                        key={email}
                        label={email}
                        onDelete={() => handleRemoveEmail(email)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </EmailChipsContainer>
                )}
                <TextField
                  value={emailDraft}
                  onChange={handleEmailInputChange}
                  onBlur={handleEmailInputBlur}
                  onKeyDown={handleEmailInputKeyDown}
                  placeholder={t("share.emailPlaceholder")}
                  fullWidth
                  autoComplete="off"
                  label={t("share.addEmails")}
                />
              </EmailInputContainer>
            </ShareSection>
            <ShareSection>
              <BaseLabel fontSize="16px" fontWeight="600">
                {t("share.message")}
              </BaseLabel>
              <Box
                sx={{
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                  padding: "16px",
                  minHeight: "200px",
                  backgroundColor: "#f9f9f9",
                  overflowY: "auto",
                  "& p": {
                    margin: 0,
                    padding: 0,
                  },
                  "& p + p": {
                    marginTop: "8px",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(emailBody, {
                    allowedTags: [
                      "p",
                      "br",
                      "strong",
                      "b",
                      "em",
                      "i",
                      "u",
                      "h1",
                      "h2",
                      "h3",
                      "h4",
                      "h5",
                      "h6",
                      "ul",
                      "ol",
                      "li",
                      "table",
                      "thead",
                      "tbody",
                      "tr",
                      "th",
                      "td",
                      "span",
                      "div",
                    ],
                    allowedAttributes: {
                      "*": ["style"],
                    },
                  }),
                }}
              />
            </ShareSection>
            <ShareSection>
              <BaseLabel fontSize="16px" fontWeight="600">
                {t("attachments.attachForDownload")}
              </BaseLabel>
              {attachmentsLoading && (
                <HelperText>{t("attachments.loading")}</HelperText>
              )}
              {!attachmentsLoading &&
                attachments.length === 0 &&
                !record?.filename && (
                  <HelperText>{t("attachments.notAvailable")}</HelperText>
                )}
              {!attachmentsLoading &&
                (attachments.length > 0 || record?.filename) && (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      marginLeft: "8px",
                    }}
                  >
                    {record?.filename && (
                      <AttachmentControlLabel
                        control={
                          <Checkbox
                            checked={includeMainRecord}
                            onChange={(event) =>
                              setIncludeMainRecord(event.target.checked)
                            }
                          />
                        }
                        label={`${
                          record?.title || formatAttachmentName(record.filename)
                        } ${t("recordings.mainRecording")}`}
                      />
                    )}
                    {attachments.map((attachment) => (
                      <AttachmentControlLabel
                        key={attachment.hearingFileId}
                        control={
                          <Checkbox
                            checked={selectedAttachmentIds.includes(
                              attachment.hearingFileId,
                            )}
                            onChange={() =>
                              toggleAttachmentSelection(
                                attachment.hearingFileId,
                              )
                            }
                          />
                        }
                        label={formatAttachmentName(attachment.filename)}
                      />
                    ))}
                  </Box>
                )}
            </ShareSection>
          </ShareDrawerContainer>
        </ShareDrawerScroll>
      </Drawer>
    </>
  );
};
